interface PreservedCellStyle {
    cell: HTMLElement
    boxSizing: string
    width: string
}

/**
 * Pins the widths of a dragged row's cells so the row does not collapse once dnd-kit lifts it
 * out of its table formatting context.
 *
 * dnd-kit already does this itself, but only for real table rows — it gates the behaviour on
 * `element.tagName === 'TR'`. Our rows are `<li>` elements with `display: table-row`, so the
 * built-in path never runs and we reproduce it here. This can be deleted if dnd-kit ever keys
 * off the computed display instead of the tag name.
 */
export const preserveDraggedTableRowLayout = (row: Element): (() => void) => {
    const cells = Array.from(row.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement && window.getComputedStyle(child).display === 'table-cell'
    )

    // Measure every cell before writing any width. Interleaving the two makes each write
    // invalidate layout, forcing a synchronous reflow on the next measurement.
    const preservedStyles = cells.map<PreservedCellStyle>(cell => ({
        cell,
        boxSizing: cell.style.boxSizing,
        width: cell.style.width,
    }))
    const widths = cells.map(cell => cell.getBoundingClientRect().width)

    cells.forEach((cell, index) => {
        cell.style.boxSizing = 'border-box'
        cell.style.width = `${widths[index]}px`
    })

    return () => {
        preservedStyles.forEach(({ cell, boxSizing, width }) => {
            cell.style.boxSizing = boxSizing
            cell.style.width = width
        })
    }
}
